<template>
  <v-card>
    <v-form v-model="formValid" ref="form">
      <v-container>
        <v-row>
          <v-col cols="7">
            <v-row dense>
              <v-col cols="4">
                <v-img :src="imageUrl" class="grey lighten-2"></v-img>
                <input
                  type="file"
                  style="display: none"
                  ref="image"
                  accept="image/*"
                  @change="onFilePicked"
                />
                <v-btn block class="primary" @click="$refs.image.click()">
                  <v-icon left>mdi-camera</v-icon>
                  {{ $t("common.edit") }}
                </v-btn>
              </v-col>
              <v-col cols="8">
                <v-text-field
                  v-model="newTravel.name"
                  :label="$t('common.name')"
                  required
                  :rules="[rules.required]"
                ></v-text-field>
              </v-col>

              <v-col cols="12" class="my-n2">
                <div style="height: 200px">
                  <quill-editor
                    class="editor mt-3"
                    ref="myQuillEditor"
                    :options="editorOption"
                    @ready="onEditorReady"
                  />
                </div>
                <h4>{{ $t("common.departure") }}</h4>
              </v-col>

              <v-col cols="6">
                <DatePickerField v-model="originDate" />
              </v-col>

              <v-col cols="6">
                <TimePickerField v-model="originTime" :rules="[rules.required]" />
              </v-col>

              <v-col cols="12" class="my-n2">
                <h4>{{ $t("common.arrival") }}</h4>
              </v-col>
              <v-col cols="6">
                <DatePickerField v-model="destinDate" />
              </v-col>

              <v-col cols="6">
                <TimePickerField v-model="destinTime" :rules="[rules.required]" />
              </v-col>

              <v-col cols="6" class="mt-1 py-0">
                <h4>{{ $t("common.notifications") }}</h4>
                <v-checkbox
                  color="primary"
                  class="ma-0"
                  v-model="newTravel.alarm"
                  :label="$t('common.notifications')"
                ></v-checkbox>
              </v-col>
              <v-col cols="6" class="mt-1 py-0">
                <h4>{{ $t("common.remindersHint") }}</h4>
                <v-select
                  class="ma-0 py-0"
                  :items="reminders"
                  v-model="newTravel.reminder"
                ></v-select>
              </v-col>

              <v-col cols="6" class="py-0">
                <v-checkbox
                  dense
                  class="ma-0"
                  color="primary"
                  persistent-hint
                  v-model="newTravel.attendance"
                  :label="$t('common.checkAttendance')"
                ></v-checkbox>
              </v-col>
              <v-col cols="6" class="py-0">
                <v-checkbox
                  dense
                  color="primary"
                  class="ma-0"
                  persistent-hint
                  v-model="newTravel.feedback"
                  :label="$t('common.feedBackLabel')"
                ></v-checkbox>
              </v-col>
            </v-row>
          </v-col>

          <v-col cols="5">
            <p v-if="newTravel.origin.address" class="white--text primary pa-2 ma-0 text-center">
              {{ $t("common.origin") }} - {{ newTravel.origin.address }}
            </p>
            <p v-if="newTravel.destin.address" class="white--text primary pa-2 ma-0 text-center">
              {{ $t("common.destination") }} - {{ newTravel.destin.address }}
            </p>
            <img
              v-if="newTravel.origin.coordinates || newTravel.destin.coordinates"
              :src="mapboxStaticImage"
              width="100%"
            />
            <v-card v-else class="grey lighten-3 d-flex align-center" style="height: 50%">
              <v-card-text style="width: 100%">
                <h2 class="text-center">{{ $t("common.noLocation") }}</h2>
              </v-card-text>
            </v-card>

            <v-dialog v-model="mapDialog" width="600">
              <template v-slot:activator="{ on }">
                <v-btn block class="fill-height" color="primary" v-on="on">
                  <v-icon left>mdi-pencil</v-icon>
                  {{ $t("common.edit") }}
                </v-btn>
              </template>

              <v-card v-if="mapDialog">
                <p
                  v-if="originMarker && originMarker.address"
                  class="white--text primary pa-2 ma-0 text-center"
                >
                  {{ $t("common.origin") }} - {{ originMarker.address }}
                </p>
                <p
                  v-if="destinMarker && destinMarker.address"
                  class="white--text primary pa-2 ma-0 text-center"
                >
                  {{ $t("common.destination") }} - {{ destinMarker.address }}
                </p>
                <MglMap
                  v-if="mapDialog"
                  :accessToken="accessToken"
                  :center.sync="mapCenter"
                  :zoom.sync="mapZoom"
                  style="width: 600px; height: 400px"
                  :mapStyle="mapStyle"
                  @load="mapLoaded"
                >
                  <MglMarker
                    v-if="originMarker"
                    anchor="bottom"
                    :color="`#${blue}`"
                    :coordinates="originMarker.coordinates"
                  ></MglMarker>

                  <MglMarker
                    v-if="destinMarker"
                    anchor="bottom"
                    :color="`#${red}`"
                    :coordinates="destinMarker.coordinates"
                  ></MglMarker>

                  <MglGeocoderControl
                    :accessToken="accessToken"
                    :mapboxgl="mapboxgl"
                    :marker="false"
                    :placeholder="$t('common.origin')"
                    @result="selectedOrigin"
                  />
                  <MglGeocoderControl
                    :accessToken="accessToken"
                    :mapboxgl="mapboxgl"
                    :marker="false"
                    :placeholder="$t('common.destination')"
                    @result="selectedDestin"
                  />
                </MglMap>
                <v-card-actions>
                  <div class="flex-grow-1"></div>

                  <v-btn color text @click="mapDialog = false">{{ $t("common.cancel") }}</v-btn>

                  <v-btn color="primary" text @click="saveNewPoints">{{ $t("common.save") }}</v-btn>
                </v-card-actions>
              </v-card>

              <h4 class="mt-4">{{ $t("common.attendance") }}</h4>
            </v-dialog>
            <v-radio-group v-model="newTravel.privacy" :hint="privacyHint" persistent-hint row grow>
              <v-radio
                v-for="type in privacy"
                :key="type.value"
                :label="type.text"
                :value="type.value"
              ></v-radio>
            </v-radio-group>
            <div class="mt-2" v-if="newTravel.privacy == 3">
              <h4>{{ $t("common.selectedUsers") }}</h4>
              <v-select
                multiple
                v-model="invitedUsers"
                :items="allUsers"
                item-text="name"
                item-value="id"
                :label="this.$t('common.attendance')"
                single-line
                @selected="doSomething($event)"
              ></v-select>
            </div>
            <div class="mt-2" v-if="newTravel.privacy == 3">
              <h4>{{ $t("itinerary.NewEvent.selectedSubgroups") }}</h4>
              <v-select
                multiple
                v-model="subgroups"
                :items="allSubgroups"
                item-text="name"
                item-value="id"
                :label="this.$t('navbar.menu.subgroups')"
                single-line
              ></v-select>
            </div>
          </v-col>
        </v-row>
        <p v-if="feedback" class="my-0 mr-4 red--text text-right">
          {{ feedback }}
        </p>
      </v-container>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :text="editActivity != null"
          :color="editActivity != null ? 'primary' : 'error'"
          @click="handleDelete"
          >{{ editActivity != null ? $t("common.cancel") : $t("common.delete") }}
        </v-btn>
        <v-btn color="primary" :disabled="!formValid" :loading="saving" @click="handleSave"
          >{{ $t("common.save") }}
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { format, parseISO } from "date-fns";
import { MglMap, MglMarker } from "v-mapbox";
import MglGeocoderControl from "@geospoc/v-mapbox-geocoder";
import firestore from "@/firebase/firestore";
import storage from "@/firebase/storage";

import Mapbox from "mapbox-gl";
import DatePickerField from "@/components/Base/DatePickerField";
import TimePickerField from "@/components/Base/TimePickerField";
import { uuid } from "vue-uuid";
import reminders from "@/assets/reminders";
import mbAPI from "@/APIS/mapbox-api";

import _ from "lodash";
import { quillEditor } from "vue-quill-editor";

// import theme style
// eslint-disable-next-line import/no-extraneous-dependencies
import "quill/dist/quill.snow.css";

export default {
  name: "ExcelUploadTravel",
  props: ["excelActivity", "editActivity"],
  components: {
    DatePickerField,
    TimePickerField,
    MglGeocoderControl,
    MglMap,
    MglMarker,
    quillEditor,
  },
  data(a) {
    return {
      editorOption: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            ["clean"],
            ["link"],
          ],
        },
        theme: "snow",
        placeholder: a.$t("common.description"),
      },

      content: "",
      originMarker: null,
      originMarketPoint: null,

      destinMarker: null,
      destinMarketPoint: null,

      mapCenter: [0, 0],
      mapZoom: 0,

      newTravel: null,

      originDate: new Date().toISOString().substr(0, 10),
      originTime: null,

      destinDate: new Date().toISOString().substr(0, 10),
      destinTime: null,

      blue: "48ace9",
      red: "ec2323",

      invitedUsers: null,
      subgroups: [],

      feedback: null,
      formValid: false,

      mapDialog: false,

      reminders: reminders.options(this.$i18n),
      privacy: [
        {
          value: 2,
          text: this.$i18n.t("common.privacy.everyone.text"),
          hint: this.$i18n.t("common.privacy.everyone.hint"),
        },
        {
          value: 1,
          text: this.$i18n.t("common.optional"),
          hint: this.$i18n.t("common.privacy.optional.hint"),
        },
        {
          value: 3,
          text: this.$i18n.t("common.selected"),
          hint: this.$i18n.t("common.privacy.selected.hint"),
        },
      ],
      rules: {
        required: (value) => !!value || "Required.",
      },

      usersMenu: false,
      lastClickedOrigin: false,
      file: null,
      imageSrc: null,
      saving: false,
    };
  },
  methods: {
    onEditorReady(quill) {
      if (this.newTravel.quill_description) {
        console.log("Quill description");
        quill.setContents(JSON.parse(this.newTravel.quill_description).ops);
      } else {
        console.log("Description");
        quill.setText(this.newTravel.description);
      }
    },
    updateUserSubgroupsTravels(invitedUsers, subgroups, privacy, reminder) {
      this.newTravel.privacy = privacy;
      this.newTravel.reminder = reminder;
      if (privacy === 3) {
        this.invitedUsers = invitedUsers;
        this.subgroups = subgroups;
      }
    },
    toBoolArray(object) {
      if (!object) return null;
      return object.reduce((obj, id) => {
        // eslint-disable-next-line no-param-reassign,indent
        obj[id] = true;
        return obj;
      }, {});
    },
    handleSave() {
      if (!this.formValid) {
        return;
      }
      this.newTravel.origin.date = parseISO(`${this.originDate} ${this.originTime}`);
      this.newTravel.destin.date = parseISO(`${this.destinDate} ${this.destinTime}`);

      console.log("deleting event");
      const tripId = this.$store.getters.currentTripId;

      if (this.newTravel.privacy === 3) {
        console.log("event is private, selecting: ", this.invitedUsers);
        if (
          (this.subgroups == null || this.subgroups.length === 0) &&
          (this.invitedUsers == null || this.invitedUsers.length === 0)
        ) {
          this.feedback = this.$t("itinerary.NewEvent.feedBack3");
          return;
        }
        this.newTravel.users = this.toBoolArray(this.invitedUsers);
        this.newTravel.subgroups = this.toBoolArray(this.subgroups);
      }

      this.newTravel.origin.coordinates = firestore.getGeo(this.newTravel.origin.coordinates);
      this.newTravel.destin.coordinates = firestore.getGeo(this.newTravel.destin.coordinates);

      let id;
      if (this.$props.editActivity) {
        id = this.$props.editActivity.id;
      }
      this.saving = true;

      this.newTravel.quill_description = JSON.stringify(
        this.$refs.myQuillEditor.quill.getContents(),
      );
      this.newTravel.description = this.$refs.myQuillEditor.quill.getText();

      firestore.writeTrackedDocument(tripId, "travels", this.newTravel, id).then((docRef) => {
        if (docRef) {
          id = docRef.id;
        }

        console.log("Document written with ID: ", id);

        if (this.file) {
          console.log("saving image file");
          const name = `${uuid.v4()}.${this.file.name.split(".").pop().toLowerCase()}`;
          const firPath = `groups/${tripId}/images/${name}`;

          storage.uploadFile(this.file, firPath).then((snapshot) => {
            console.log("Uploaded a blob or file!", snapshot);
            firestore.writeTrackedDocument(
              tripId,
              "travels",
              {
                "files.header": firPath,
              },
              id,
            );
            this.saving = false;
            this.$emit("remove", true);
          });
        } else {
          console.log("no image, exit");
          this.saving = false;
          this.$emit("remove", true);
        }
      });
    },
    handleDelete() {
      console.log("deleting event");
      this.$emit("remove", false);
    },
    selectedOrigin(event) {
      console.log("selected item");
      console.log(event.result);
      const wikiCode =
        event.result.properties.wikiData ||
        event.result.context.find((context) => context.wikidata).wikidata;
      this.originMarker = {
        coordinates: event.result.center,
        address: event.result.place_name,
        wiki_code: wikiCode,
      };
    },
    selectedDestin(event) {
      console.log("selected item");
      console.log(event.result);
      const wikiCode =
        event.result.properties.wikiData ||
        event.result.context.find((context) => context.wikidata).wikidata;
      this.destinMarker = {
        coordinates: event.result.center,
        address: event.result.place_name,
        wiki_code: wikiCode,
      };
    },
    mapLoaded(payload) {
      console.log("map loaded!");

      if (this.newTravel.origin.coordinates) {
        this.originMarker = {
          coordinates: [
            this.newTravel.origin.coordinates.long,
            this.newTravel.origin.coordinates.lat,
          ],
          address: this.newTravel.origin.address,
        };
      }
      if (this.newTravel.destin.coordinates) {
        this.destinMarker = {
          coordinates: [
            this.newTravel.destin.coordinates.long,
            this.newTravel.destin.coordinates.lat,
          ],
          address: this.newTravel.destin.address,
        };
      }

      payload.map.on("click", (e) => {
        const { lngLat } = e;

        console.log("clicked on", lngLat);

        const marker = { coordinates: [lngLat.lng, lngLat.lat] };

        if (this.originMarker == null) {
          console.log("setting origin");
          this.originMarker = marker;
        } else if (this.destinMarker == null || this.lastClickedOrigin) {
          console.log("setting destin");
          this.destinMarker = marker;
          this.lastClickedOrigin = false;
        } else {
          console.log("setting origin, else lastclicked ");
          this.originMarker = marker;
          this.lastClickedOrigin = true;
        }

        mbAPI.reverseGeocode(lngLat.lat, lngLat.lng).then((result) => {
          console.log("reversGeocode->", result);
          this.$set(marker, "address", result.name);
          this.$set(marker, "wiki_code", result.wiki_code);
        });
      });
    },
    saveNewPoint(point, marker) {
      this.$set(this.newTravel[point], "coordinates", {
        lat: marker.coordinates[1],
        long: marker.coordinates[0],
      });
      this.$set(this.newTravel[point], "address", marker.address);
      this.$set(this.newTravel[point], "wiki_code", marker.wiki_code);

      mbAPI.getTimezone(marker.coordinates[0], marker.coordinates[1]).then((timezone) => {
        this.$set(this.newTravel[point], "time_zone", timezone);
      });
    },
    saveNewPoints() {
      this.mapDialog = false;

      if (this.originMarker) {
        this.saveNewPoint("origin", this.originMarker);
      }
      if (this.destinMarker) {
        this.saveNewPoint("destin", this.destinMarker);
      }
    },
    setupEditTravel(event) {
      console.log(event);
      this.newTravel = {
        name: event.name,
        description: event.description,
        quill_description: event.quill_description,
        reminder: event.reminder,
        alarm: event.alarm,
        origin: _.clone(event.origin),
        destin: _.clone(event.destin),
        privacy: event.privacy,
        feedback: event.feedback,
        attendance: event.attendance,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      if (event.privacy === 3) {
        this.invitedUsers = Object.keys(event.users);
        this.subgroups = Object.keys(event.subgroups);
      }
      if (event.origin.coordinates) {
        this.newTravel.origin.coordinates = {
          lat: event.origin.coordinates.latitude,
          long: event.origin.coordinates.longitude,
        };
        this.mapCenter = [
          this.newTravel.origin.coordinates.long,
          this.newTravel.origin.coordinates.lat,
        ];
        this.mapZoom = 13;
      }
      if (event.destin.coordinates) {
        this.newTravel.destin.coordinates = {
          lat: event.destin.coordinates.latitude,
          long: event.destin.coordinates.longitude,
        };
      }

      this.originDate = format(event.origin.date.toDate(), "yyyy-MM-dd");
      this.originTime = format(event.origin.date.toDate(), "HH:mm");
      this.destinDate = format(event.destin.date.toDate(), "yyyy-MM-dd");
      this.destinTime = format(event.destin.date.toDate(), "HH:mm");
    },
    setupExcelTravel(event) {
      console.log("excelUploadTravle ->created");
      this.newTravel = {
        name: event.name,
        description: event.description,
        reminder: event.reminder,
        alarm: event.alarm,
        origin: event.origin,
        destin: event.destin,
        privacy: 1,
        feedback: false,
        attendance: false,
        time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };
      console.log(this.newTravel);
      this.originDate = format(event.origin.date, "yyyy-MM-dd");
      this.originTime = format(event.origin.date, "HH:mm");
      this.destinDate = format(event.destin.date, "yyyy-MM-dd");
      this.destinTime = format(event.destin.date, "HH:mm");
    },
    onFilePicked(e) {
      const { files } = e.target;
      if (files[0] !== undefined) {
        const fr = new FileReader();
        fr.readAsDataURL(files[0]);
        fr.addEventListener("load", () => {
          [this.file] = files;
          this.imageSrc = fr.result;
        });
      }
    },
  },
  computed: {
    allUsers() {
      return this.$store.getters.allUsers;
    },
    allSubgroups() {
      return Object.values(this.$store.state.trip.subgroups);
    },
    privacyHint() {
      const { privacy } = this.newTravel;
      console.log(privacy);
      return this.privacy.filter((item) => item.value === privacy)[0].hint;
    },
    mapboxgl() {
      return Mapbox;
    },
    accessToken() {
      return mbAPI.accessToken;
    },
    mapStyle() {
      return mbAPI.mapStyle;
    },
    imageUrl() {
      const event = this.$props.editActivity;
      const img = event ? this.$store.state.imageUrls[event.id] : require("@/assets/other.jpg");
      return this.imageSrc || img || require("@/assets/other.jpg");
    },
    mapboxStaticImage() {
      const origin = this.newTravel.origin.coordinates;
      const destin = this.newTravel.destin.coordinates;
      if (!(origin && origin.lat) && !(destin && destin.lat)) {
        return null;
      }

      let markers = "";
      let zoom = "";
      if (origin) {
        markers += `pin-s+${this.blue}(${origin.long},${origin.lat})`;
        zoom = `${origin.long},${origin.lat},15`;
      }
      if (destin) {
        if (origin) {
          markers += ",";
          zoom = "auto";
        } else {
          zoom = `${destin.long},${destin.lat},15`;
        }

        markers += `pin-s+${this.red}(${destin.long},${destin.lat})`;
      }

      const url = `https://api.mapbox.com/styles/v1/mapbox/streets-v11/static/${markers}/${zoom}/700x350?access_token=${this.accessToken}`;
      return url;
    },
  },
  created() {
    console.log("excelUploadTravle ->created");
    const excelTravel = this.$props.excelActivity;
    if (excelTravel) {
      this.setupExcelTravel(excelTravel);
    } else {
      this.setupEditTravel(this.$props.editActivity);
    }
  },
};
</script>
